import { Form, Formik, FormikHelpers } from "formik";
import moment from "moment";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import {
  useEditStudentMutation,
  useViewStudentByIdQuery,
} from "src/services/StudentServices";
import { showToast } from "src/utils/showToaster";
import { object, string } from "yup";
import StudentVerticalStepper from "./VerticalStepper/VerticalStepper";
import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { mobileRegExp } from "src/utils/validations/indianPhoneRegex";

const EditStudentFormWrapper = () => {
  const { studentId = "" } = useParams();
  const [studentData, setStudentData] = useState<any>();

  const [editStudent] = useEditStudentMutation();
  const { data, isLoading, isFetching } = useViewStudentByIdQuery(studentId);

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setStudentData(data?.data);
    }
  }, [data, isLoading, isFetching]);
  // Form Initial Values
  const initialValues: any = {
    name: studentData?.name,
    username : studentData?.username ,
    mobile: studentData?.mobile,
    email: studentData?.email,
    instagram_id: studentData?.instagram_id,
    whatsApp_mobile: studentData?.whatsApp_mobile,
    emergency_contact_no: studentData?.emergency_contact_no,
    facebook_id: studentData?.facebook_id,
    branchId:
      studentData?.branchId?.map((branch: any) => {
        return {
          label: branch?.branchName,
          value: branch?._id,
        };
      }) || [],
    youtube_id: studentData?.youtube_id,
    twitter_id: studentData?.twitter_id,
    linkedin_id: studentData?.linkedin_id,
    dob: studentData?.dob,
    marital_status: studentData?.marital_status,
    gender: studentData?.gender,
    address: studentData?.address,
    academic_history: studentData?.academic_history?.map((el: any) => {
      return {
        collge_school_name: el?.collge_school_name,
        course_name: el?.course_name,
        course_type: el?.course_type,
        collge_school_tier: el?.collge_school_tier,
        start_year: el?.start_year,
        end_year: el?.end_year,
        currently_pursuing: el?.currently_pursuing,
      };
    }),
    work_history: studentData?.work_history?.map((el: any) => {
      return {
        company_name: el?.company_name,
        job_title: el?.job_title,
        job_description: el?.job_description,
        start_year: el?.start_year,
        end_year: el?.end_year,
        currently_pursuing: el?.currently_pursuing,
      };
    }),
    countryId: {
      countryName: studentData?.countryLabel,
      _id: studentData?.countryId,
    },
    stateId: {
      stateName: studentData?.stateLabel,
      _id: studentData?.stateId,
    },
    cityId: {
      cityName: studentData?.cityLabel,
      _id: studentData?.cityId,
    },
    courses: studentData?.courses,
  };

  // Validation Schema
  const validationSchema = object().shape({
    mobile: string()
      .trim()
      .matches(mobileRegExp, "mobile number is not valid")
      .required("Please enter mobile number"),
    whatsApp_mobile: string()
      .trim()
      .matches(mobileRegExp, "mobile number is not valid"),
    emergency_contact_no: string()
      .trim()
      .matches(mobileRegExp, "mobile number is not valid"),
      email:string().email().required('email is required'),
    name: string().required("Please enter name"),
    // address: string().required("Please enter address"),
    // dob: string().required("Please enter date of birth"),
    stateId: object().required("please select state"),
    cityId: object().required("please select city"),
  });

  // Handle Submit
  const handleSubmit = (
    values: any,
    { setSubmitting, resetForm }: FormikHelpers<any>
  ) => {
    setSubmitting(true);
    // Step 1: Remove the country code if present
    const withoutCountryCode = values?.mobile?.replace(/^\+91\s?/, "");

    // Step 2: Remove all spaces
    const finalNumber = withoutCountryCode?.replace(/\s/g, "");

    const formattedValues = {
      ...values,
      mobile: finalNumber,
      dob: values?.dob ? moment(values?.dob).format("YYYY-MM-DD") : "",
      branchId: values?.branchId?.map((branch: any) => branch?.value),
      work_history: values?.work_history?.map((data: any) => {
        return {
          ...data,
          start_year: data?.start_year ? moment(data?.start_year).year()?.toString() : "",
          end_year: data?.end_year ? moment(data?.end_year).year()?.toString() : "",
        };
      }),
      academic_history: values?.academic_history?.map((data: any) => {
        return {
          ...data,
          start_year: data?.start_year ? moment(data?.start_year).year()?.toString() : "",
          end_year: data?.end_year ? moment(data?.end_year).year()?.toString() : "",
        };
      }),
      countryId: values?.countryId?._id,
      stateId: values?.stateId?._id,
      cityId: values?.cityId?._id,
    };
    const { courses, ...restFormatedValues } = formattedValues;
    editStudent({
      body: restFormatedValues,
      id: studentId,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
        setSubmitting(false);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
        } else {
          showToast("error", res?.data?.message);
          setSubmitting(false);
        }
      }
      setSubmitting(false);
    });
   
  };

  return (
    <SideNavLayout>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form className="h-full  overflow-hidden">
            <div className="h-full flex flex-col">
              {(isLoading || isFetching) && (
                <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                  <CircularProgress />
                </div>
              )}
              <div className="border-b border-slate-300 p-4">
                <div className="flex gap-3">
                  {/* Avatar  */}
                  <div className="w-[56px] aspect-square rounded bg-primary-main text-white flex justify-center items-center text-2xl font-semibold capitalize ">
                    {formikProps?.values?.name?.[0]}{" "}
                  </div>

                  <div className="flex flex-col">
                    {/* Name */}
                    <div className="text-primary-dark font-semibold text-xl capitalize">
                      {formikProps?.values?.name}
                    </div>

                    {/* Mobile */}
                    <div className="text-primary-dark d text-slate-600">
                      {formikProps?.values?.mobile}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-auto px-4">
                <StudentVerticalStepper formikProps={formikProps} />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </SideNavLayout>
  );
};

export default EditStudentFormWrapper;
